/**
 * dd-line-mutation.js — THE shared line-quantity transaction controller.
 *
 * One implementation of the qty/remove path for every cart surface (mini-cart
 * drawer + cart page). Before this module, dd-mini-cart.js and dd-cart-page.js
 * each owned a private copy of: debounce map, identity resolution, prev-qty
 * resolution, busy lifecycle, success-edge toasts, and a divergent error
 * pipeline (the drawer had 400-retry + 422 server re-read; the page did not).
 *
 * Responsibilities here:
 *   1. Debounce/coalesce qty intents per line (rapid +/- clicks).
 *   2. Optimistic line paint (kill switch: window.DD_OPTIMISTIC_LINE_PAINT)
 *      using cart-paint's predictLineItem / paintLineFromItem, with
 *      snapshot/rollback sourced from the last SERVER-COMMITTED cart.
 *   3. The mutation itself via helpers.mutateCartChange (queue-owned:
 *      hooks -> authoritative cart -> section settle -> single publish).
 *   4. Success-edge handling (inventory cap adjustments) and the full error
 *      pipeline: stock-edge parse, 400 stale-key retry with fresh identity,
 *      422 server re-read, stock auto-adjust — now shared by BOTH surfaces.
 *   5. Per-line busy lifecycle, delegated to surface adapters (each surface
 *      styles its own pending affordance).
 *
 * Surfaces register an adapter:
 *   window.DDLineMutation.registerSurface('drawer', {
 *     setLineBusy(lineKey, busy),   // per-line pending affordance
 *     clearAllBusy(),               // safety sweep
 *     announce(msg),                // aria-live region
 *     afterSuccess(cart),           // optional surface-specific paint hooks
 *     refresh(),                    // optional last-resort surface refresh
 *   });
 *
 * And route their DOM events into:
 *   window.DDLineMutation.requestQtyChange({
 *     surface, lineKey, lineNumber, qty, itemTitle, previousQty, sourceEl
 *   });
 */
(function () {
  'use strict';

  if (window.DDLineMutation) return;

  var LINE_QTY_DEBOUNCE_MS = 120;
  var PROTECTION_PRODUCT_ID = 10266015924541;

  var _surfaces = {};
  var _pending = {};        /* lineKey -> debounced intent */
  var _tokens = {};         /* lineKey -> latest mutation token */

  function helpers() { return window.ddCartHelpers || {}; }
  function shared() { return (window.__ddCartShared = window.__ddCartShared || {}); }

  function optimisticEnabled() {
    return window.DD_OPTIMISTIC_LINE_PAINT !== false;
  }

  function adapterFor(name) {
    return _surfaces[name || ''] || _surfaces.drawer || {};
  }

  function announce(adapter, msg) {
    if (adapter.announce) { adapter.announce(msg); return; }
    var el = document.getElementById('cart-live-region-text');
    if (el) el.textContent = msg;
  }

  function toast(msg, type) {
    if (msg && window.ddToast) window.ddToast(msg, { type: type || 'info' });
  }

  /* ── DOM identity helpers (shared verbatim semantics from dd-mini-cart) ── */

  function findQtyInput(lineKey) {
    if (!lineKey) return null;
    return document.querySelector('[data-dd-qty-input][data-line-key="' + lineKey + '"]');
  }

  function resolveLineIdentity(lineKey, lineNumber, sourceEl) {
    var resolvedKey = lineKey ? String(lineKey) : '';
    var resolvedLine = parseInt(lineNumber || '0', 10) || 0;
    var row = sourceEl && sourceEl.closest ? sourceEl.closest('[data-dd-cart-item]') : null;
    if (!row && resolvedKey) {
      var rows = document.querySelectorAll('[data-dd-cart-item]');
      for (var i = 0; i < rows.length; i++) {
        if ((rows[i].dataset && rows[i].dataset.lineKey) === resolvedKey) { row = rows[i]; break; }
      }
    }
    if (row && row.dataset) {
      if (!resolvedKey && row.dataset.lineKey) resolvedKey = String(row.dataset.lineKey);
      if (!resolvedLine && row.dataset.line) {
        var parsedLine = parseInt(row.dataset.line, 10);
        if (parsedLine > 0) resolvedLine = parsedLine;
      }
    }
    return { lineKey: resolvedKey, lineNumber: resolvedLine };
  }

  function resolveVariantIdFromSource(lineKey, sourceEl) {
    var row = sourceEl && sourceEl.closest ? sourceEl.closest('[data-dd-cart-item]') : null;
    if (!row && lineKey) {
      row = document.querySelector('[data-dd-cart-item][data-line-key="' + lineKey + '"]');
    }
    if (!row) return 0;
    return parseInt(row.getAttribute('data-variant-id') || '0', 10) || 0;
  }

  function setLineInputToQty(lineKey, qty) {
    if (qty == null || isNaN(qty) || !lineKey) return null;
    var input = findQtyInput(lineKey);
    if (!input) return null;
    var next = parseInt(qty, 10);
    if (isNaN(next) || next < 0) next = 0;
    input.value = String(next);
    input.setAttribute('data-dd-prev-qty', String(next));
    return input;
  }

  /* ── Cart-shape helpers ── */

  function isProtectionLikeItem(item) {
    var h = helpers();
    if (h.isProtectionLine) {
      try { if (h.isProtectionLine(item)) return true; } catch (_) {}
    }
    if (!item) return false;
    if (Number(item.product_id || 0) === PROTECTION_PRODUCT_ID) return true;
    var props = item.properties && typeof item.properties === 'object' ? item.properties : null;
    if (props && (props._dd_shipping_protection || props.dd_shipping_protection)) return true;
    return false;
  }

  function getCommittedQtyFromCart(cart, lineKey) {
    var items = Array.isArray(cart && cart.items) ? cart.items : [];
    if (!items.length || !lineKey) return null;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].key || '') === String(lineKey)) {
        return parseInt(items[i].quantity, 10) || 0;
      }
    }
    return null;
  }

  function findItemInCart(cart, lineKey) {
    var items = Array.isArray(cart && cart.items) ? cart.items : [];
    for (var i = 0; i < items.length; i++) {
      if (items[i] && String(items[i].key || '') === String(lineKey)) return items[i];
    }
    return null;
  }

  /* ── Error classification (single copy — was duplicated/divergent) ── */

  function getErrorStatus(err) {
    return Number(
      (err && (err.shopifyStatus || err.status)) ||
      (err && err.responseBody && err.responseBody.status) || 0
    );
  }

  function getErrorText(err) {
    var parts = [];
    if (err && err.responseBody) {
      if (err.responseBody.description) parts.push(String(err.responseBody.description));
      if (err.responseBody.message) parts.push(String(err.responseBody.message));
      if (err.responseBody.errors) {
        if (typeof err.responseBody.errors === 'string') parts.push(err.responseBody.errors);
        else { try { parts.push(JSON.stringify(err.responseBody.errors)); } catch (_) {} }
      }
    }
    if (err && err.description) parts.push(String(err.description));
    if (err && err.message) parts.push(String(err.message));
    return parts.join(' ').replace(/[\u2018\u2019]/g, "'");
  }

  function parseEdgeConstraint(err, requestedQty, previousQty) {
    var h = helpers();
    if (!h.parseCartStockError) return null;
    var parsed = h.parseCartStockError(err, {
      operation: 'change', requestedQty: requestedQty, previousQty: previousQty
    });
    if (!parsed || parsed.kind === 'stale-line') return null;
    var stockMsg = h.stockErrorMessage(parsed, { operation: 'change' });
    return {
      message: stockMsg ? stockMsg.message : null,
      toastType: stockMsg ? stockMsg.toastType : 'error',
      allowedQty: parsed.allowedQty
    };
  }

  function buildFallbackToast(err, requestedQty, previousQty, isRemoval) {
    var h = helpers();
    var parsed = h.parseCartStockError
      ? h.parseCartStockError(err, { operation: 'change', requestedQty: requestedQty, previousQty: previousQty })
      : null;
    if (parsed) {
      var stockMsg = h.stockErrorMessage(parsed, { operation: 'change' });
      if (stockMsg) return { message: stockMsg.message, type: stockMsg.toastType || 'error' };
    }
    var status = getErrorStatus(err);
    var lowered = getErrorText(err).toLowerCase();
    if (
      status === 400 ||
      /no valid id or line parameter|line item not found|line .* invalid|cart error/.test(lowered)
    ) {
      return {
        message: 'Cart changed in the background. Please try that quantity update again.',
        type: 'info'
      };
    }
    if (err && err.description) return { message: String(err.description), type: 'error' };
    if (isRemoval) return { message: 'Couldn\u2019t remove item \u2014 please try again', type: 'error' };
    return { message: 'Couldn\u2019t update cart \u2014 please try again', type: 'error' };
  }

  function deriveSuccessEdge(cart, lineKey, requestedQty) {
    if (!(requestedQty > 0)) return null;
    var committedQty = getCommittedQtyFromCart(cart, lineKey);
    if (committedQty == null) return null;
    if (committedQty >= requestedQty) return { committedQty: committedQty };
    if (committedQty > 0) {
      return {
        committedQty: committedQty,
        message: 'Only ' + committedQty + ' available \u2014 quantity has been adjusted.',
        toastType: 'info'
      };
    }
    return {
      committedQty: committedQty,
      message: 'No more stock is available for this option.',
      toastType: 'error'
    };
  }

  function readCommittedQtyFromServer(lineKey) {
    var h = helpers();
    var cartPromise = h.ensureFreshCart
      ? h.ensureFreshCart({ forceNetwork: true })
      : fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error('Cart read failed (' + r.status + ')');
          return r.json();
        });
    return cartPromise.then(function (cart) {
      return getCommittedQtyFromCart(cart, lineKey);
    });
  }

  /* Stale-key recovery: a 400 usually means the line key rotated (property
     hash / discount recompute). Re-read the cart, find the same variant,
     retry against the fresh key. */
  function retryWithFreshIdentity(variantId, attemptedKey, qty) {
    if (!variantId) return Promise.resolve(null);
    var h = helpers();
    var cartPromise = h.ensureFreshCart
      ? h.ensureFreshCart({ forceNetwork: true })
      : fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (r) {
          if (!r.ok) throw new Error('Retry cart read failed (' + r.status + ')');
          return r.json();
        });
    return cartPromise
      .then(function (cart) {
        var items = Array.isArray(cart && cart.items) ? cart.items : [];
        var matches = [];
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          if (isProtectionLikeItem(it)) continue;
          if (Number(it.variant_id) === Number(variantId)) matches.push({ item: it, line: i + 1 });
        }
        if (!matches.length) return null;
        var pick = matches[0];
        if (matches.length > 1 && attemptedKey) {
          var colonIdx = attemptedKey.indexOf(':');
          var propsHash = colonIdx > 0 ? attemptedKey.substring(colonIdx + 1) : '';
          for (var m = 0; m < matches.length; m++) {
            var mKey = matches[m].item.key || '';
            if (mKey === attemptedKey) { pick = matches[m]; break; }
            var mColon = mKey.indexOf(':');
            var mHash = mColon > 0 ? mKey.substring(mColon + 1) : '';
            if (propsHash && mHash === propsHash) { pick = matches[m]; break; }
          }
        }
        var fresh = pick.item;
        if (fresh.key === attemptedKey) return null;
        return helpers().mutateCartChange(
          { id: fresh.key, quantity: qty },
          {
            granular: qty !== 0,
            action: 'line-qty-retry',
            coalesceKey: helpers().makeCoalesceKey('line', fresh.key || variantId)
          }
        );
      })
      .catch(function (err) {
        var status = Number(err && err.shopifyStatus);
        if (status === 422) throw err;
        console.warn('[dd-line-mutation] line-qty retry failed', err);
        return null;
      });
  }

  /* ── Optimistic paint ─────────────────────────────────────────────────
     Paint the new qty (and, for customers, the predicted price) into every
     rendered row for this line BEFORE the network round-trip. Snapshot from
     the last server-committed cart; rollback on error. Skipped for:
       - removals (structure changes always wait for the server)
       - protection lines / free-gift ($0) lines / selling-plan lines
       - lines missing from the committed cart (no basis to predict from) */
  function tryOptimisticPaint(lineKey, qty) {
    var h = helpers();
    if (!optimisticEnabled()) return null;
    if (!h.paintLineFromItem || !h.predictLineItem || !h.getLatestCart) return null;
    if (!(qty > 0)) return null;
    var cart = h.getLatestCart();
    var item = findItemInCart(cart, lineKey);
    if (!item) return null;
    if (isProtectionLikeItem(item)) return null;
    if (item.selling_plan_allocation) return null;
    var isGiftZero = item.final_line_price === 0 && (item.original_line_price || 0) > 0;
    var isCustomer = document.documentElement.getAttribute('data-glass-customer') === 'true';
    var snapshot = h.captureLineSnapshot ? h.captureLineSnapshot(lineKey) : null;
    try {
      var predicted = h.predictLineItem(item, qty);
      h.paintLineFromItem(lineKey, item, qty, {
        paintPrice: isCustomer && !isGiftZero,
        priceHtml: predicted ? predicted.html : undefined
      });
      return { snapshot: snapshot, predicted: predicted, item: item };
    } catch (err) {
      console.warn('[dd-line-mutation] optimistic paint failed', err);
      return null;
    }
  }

  function recordPredictionResult(optimistic, cart, lineKey, qty) {
    if (!optimistic || !optimistic.predicted) return;
    var serverItem = findItemInCart(cart, lineKey);
    if (!serverItem || serverItem.quantity !== qty) return; /* edge-adjusted: settle repaints anyway */
    var h = helpers();
    if (!h.computeLinePricing) return;
    try {
      var actual = h.computeLinePricing(serverItem);
      if (actual.saleCents !== optimistic.predicted.saleCents) {
        var s = shared();
        s.predictionMissCount = (s.predictionMissCount || 0) + 1;
        s.lastPredictionMiss = {
          lineKey: lineKey,
          predicted: optimistic.predicted.saleCents,
          actual: actual.saleCents,
          ts: Date.now()
        };
      }
    } catch (_) {}
  }

  function rollbackOptimistic(optimistic) {
    if (!optimistic || !optimistic.snapshot) return;
    var h = helpers();
    if (h.restoreLineSnapshot) h.restoreLineSnapshot(optimistic.snapshot);
  }

  /* ── Token bookkeeping (drop stale callbacks after coalesce) ── */

  function nextToken(lineKey) {
    var next = (_tokens[lineKey] || 0) + 1;
    _tokens[lineKey] = next;
    return next;
  }

  function isLatest(lineKey, token) {
    return (_tokens[lineKey] || 0) === token;
  }

  /* ── The transaction ── */

  function executeUpdate(ctx) {
    var h = helpers();
    var adapter = adapterFor(ctx.surface);
    var identity = resolveLineIdentity(ctx.lineKey, ctx.lineNumber, ctx.sourceEl);
    var lineKey = identity.lineKey;
    var lineNumber = identity.lineNumber;
    if (!lineKey) {
      if (adapter.refresh) adapter.refresh();
      return;
    }

    var qty = ctx.qty;
    var isRemoval = qty === 0;
    var prevQty = ctx.previousQty;
    var inputEl = findQtyInput(lineKey);
    if (prevQty == null && inputEl) {
      var inputPrev = parseInt(inputEl.getAttribute('data-dd-prev-qty') || '', 10);
      if (!isNaN(inputPrev) && inputPrev >= 0) prevQty = inputPrev;
    }
    if (prevQty == null && h.getLatestCart) {
      var latestQty = getCommittedQtyFromCart(h.getLatestCart(), lineKey);
      if (latestQty != null) prevQty = latestQty;
    }

    /* Inventory clamp (root cause of the gift-claim revert): our folded
       /cart/update.js path is NOT inventory-validated by Shopify, so an
       over-stock qty gets stored silently and then clamped server-side by the
       next /cart/add.js (e.g. BOGOS gift claim), yanking the subtotal below
       promo thresholds. Liquid renders the cap as data-dd-max-qty; enforce it
       here so an over-stock request never leaves the client. */
    var clampNotice = null;
    if (!isRemoval && inputEl) {
      var maxQty = parseInt(inputEl.getAttribute('data-dd-max-qty') || '', 10);
      if (!isNaN(maxQty) && maxQty >= 0 && qty > maxQty) {
        if (prevQty != null && maxQty <= prevQty) {
          /* No room to grow: revert locally, no network call. Same shared
             message the PDP/sticky-footer ATC shows at the stock cap
             (cart-errors.js stockErrorMessage, alreadyInCart branch). */
          setLineInputToQty(lineKey, prevQty);
          var capMsg = h.stockErrorMessage
            ? h.stockErrorMessage({ alreadyInCart: true }, { operation: 'add' })
            : null;
          announce(adapter, capMsg ? capMsg.message : 'You already have the maximum available in your cart.');
          toast(
            capMsg ? capMsg.message : 'You already have the maximum available in your cart.',
            capMsg ? capMsg.toastType : 'info'
          );
          return;
        }
        qty = maxQty;
        ctx.qty = maxQty;
        clampNotice = 'Only ' + maxQty + ' available, quantity has been adjusted.';
      }
    }

    var token = nextToken(lineKey);
    if (adapter.clearAllBusy) adapter.clearAllBusy();
    if (adapter.setLineBusy) adapter.setLineBusy(lineKey, true);

    var optimistic = tryOptimisticPaint(lineKey, qty);

    var body = { quantity: qty, id: lineKey };
    return h.mutateCartChange(body, {
      granular: !isRemoval,
      action: isRemoval ? 'line-remove' : 'line-qty-change',
      coalesceKey: h.makeCoalesceKey('line', lineKey || 'unknown')
    })
      .then(function (data) {
        if (!isLatest(lineKey, token)) return;
        recordPredictionResult(optimistic, data, lineKey, qty);
        var successEdge = isRemoval ? null : deriveSuccessEdge(data, lineKey, qty);
        if (isRemoval) {
          announce(adapter, ctx.itemTitle ? (ctx.itemTitle.trim() + ' removed') : 'Item removed');
        } else if (successEdge && typeof successEdge.committedQty === 'number' && !isNaN(successEdge.committedQty)) {
          setLineInputToQty(lineKey, Math.max(0, successEdge.committedQty));
          announce(adapter, 'Cart updated');
          toast(successEdge.message, successEdge.toastType);
        } else {
          var freshInput = findQtyInput(lineKey);
          if (freshInput) {
            var serverVal = parseInt(freshInput.value, 10);
            if (!isNaN(serverVal) && serverVal >= 0) freshInput.setAttribute('data-dd-prev-qty', String(serverVal));
          }
          announce(adapter, 'Cart updated');
          if (clampNotice) {
            setLineInputToQty(lineKey, qty);
            toast(clampNotice, 'info');
          }
        }
        if (adapter.afterSuccess && data) adapter.afterSuccess(data, ctx);
      })
      .catch(function (err) {
        if (!isLatest(lineKey, token)) return;
        rollbackOptimistic(optimistic);

        var is400 = getErrorStatus(err) === 400;
        var variantId = resolveVariantIdFromSource(lineKey, ctx.sourceEl);
        if (is400 && variantId) {
          return retryWithFreshIdentity(variantId, lineKey, qty)
            .then(function (retryData) {
              if (!retryData) {
                if (prevQty != null) setLineInputToQty(lineKey, prevQty);
                if (adapter.refresh) adapter.refresh();
                return;
              }
              var retryEdge = deriveSuccessEdge(retryData, lineKey, qty);
              if (retryEdge && typeof retryEdge.committedQty === 'number' && retryEdge.committedQty < qty) {
                setLineInputToQty(lineKey, retryEdge.committedQty);
                announce(adapter, 'Cart updated');
                toast(retryEdge.message, retryEdge.toastType);
                return;
              }
              announce(adapter, 'Cart updated');
              if (adapter.afterSuccess && retryData) adapter.afterSuccess(retryData, ctx);
            })
            .catch(function (retryErr) {
              if (!isLatest(lineKey, token)) return;
              var edge = parseEdgeConstraint(retryErr, qty, prevQty);
              if (prevQty != null) setLineInputToQty(lineKey, prevQty);
              if (edge && edge.message) {
                announce(adapter, 'Cart updated');
                toast(edge.message, edge.toastType);
                return;
              }
              if (adapter.refresh) adapter.refresh();
            });
        }

        var edge = parseEdgeConstraint(err, qty, prevQty);
        if (edge) {
          return handleEdgeConstraint(adapter, ctx, lineKey, token, qty, prevQty, edge, getErrorStatus(err));
        }

        var fallback = buildFallbackToast(err, qty, prevQty, isRemoval);
        var liveInput = findQtyInput(lineKey);
        if (liveInput && prevQty != null) {
          liveInput.value = String(prevQty);
          liveInput.setAttribute('data-dd-prev-qty', String(prevQty));
        }
        announce(adapter, 'Could not update cart');
        toast(fallback.message, fallback.type || 'error');
      })
      .finally(function () {
        if (!isLatest(lineKey, token)) return;
        var unlock = function () {
          if (adapter.clearAllBusy) adapter.clearAllBusy();
          if (adapter.afterSettle) adapter.afterSettle(ctx);
        };
        if (h.waitForIdle) h.waitForIdle(4500).then(unlock);
        else unlock();
      });
  }

  /* Inventory-edge resolution: adjust to the allowed qty (a follow-up
     mutation when the server rejected rather than clamped), or revert. */
  function handleEdgeConstraint(adapter, ctx, lineKey, token, qty, prevQty, edge, errStatus) {
    var h = helpers();
    var edgeAllowed = typeof edge.allowedQty === 'number' && !isNaN(edge.allowedQty)
      ? Math.max(0, edge.allowedQty) : null;
    var isIncreaseAttempt = prevQty != null ? qty > prevQty : qty > 1;

    var finalizeEdge = function () {
      if (edgeAllowed != null && edgeAllowed === qty) {
        setLineInputToQty(lineKey, edgeAllowed);
        announce(adapter, 'Cart updated');
        return;
      }
      if (edgeAllowed != null && edgeAllowed !== qty) {
        if (prevQty != null && edgeAllowed === prevQty) {
          setLineInputToQty(lineKey, edgeAllowed);
          announce(adapter, 'Cart updated');
          toast(edge.message, edge.toastType);
          return;
        }
        return h.mutateCartChange(
          { quantity: edgeAllowed, id: lineKey },
          {
            granular: edgeAllowed !== 0,
            action: edgeAllowed === 0 ? 'line-remove' : 'line-qty-stock-adjust',
            coalesceKey: h.makeCoalesceKey('line', lineKey || 'unknown')
          }
        )
          .then(function () {
            if (!isLatest(lineKey, token)) return;
            setLineInputToQty(lineKey, edgeAllowed);
            announce(adapter, 'Cart updated');
            toast(edge.message, edge.toastType);
          })
          .catch(function () {
            if (!isLatest(lineKey, token)) return;
            if (prevQty != null) setLineInputToQty(lineKey, prevQty);
            announce(adapter, 'Could not update cart');
            toast(edge.message || 'No more stock is available for this option.', 'error');
          });
      }
      if (prevQty != null) setLineInputToQty(lineKey, prevQty);
      announce(adapter, 'Could not update cart');
      toast(edge.message || 'No more stock is available for this option.', edge.toastType || 'error');
    };

    /* 422 on an increase: the server knows a committed qty we don't — read it
       before deciding whether to adjust down, revert, or accept. */
    if (errStatus === 422 && isIncreaseAttempt) {
      return readCommittedQtyFromServer(lineKey)
        .then(function (serverQty) {
          if (!isLatest(lineKey, token)) return;
          if (typeof serverQty !== 'number' || isNaN(serverQty)) return;
          if (serverQty > 0 && serverQty < qty) {
            edgeAllowed = serverQty;
            edge.message = 'Only ' + serverQty + ' available \u2014 quantity has been adjusted.';
            edge.toastType = 'info';
            return;
          }
          if (prevQty != null && serverQty <= prevQty) {
            edgeAllowed = prevQty;
            edge.message = serverQty === 0
              ? 'This item is sold out for the selected option.'
              : 'No more stock is available for this option.';
            edge.toastType = 'error';
            return;
          }
          if (serverQty >= qty) edgeAllowed = qty;
        })
        .catch(function () {})
        .then(function () { return finalizeEdge(); });
    }
    return finalizeEdge();
  }

  /* ── Public API ── */

  function requestQtyChange(req) {
    var identity = resolveLineIdentity(req.lineKey, req.lineNumber, req.sourceEl);
    var lineKey = identity.lineKey;
    if (!lineKey) {
      var adapter = adapterFor(req.surface);
      if (adapter.refresh) adapter.refresh();
      return;
    }
    var ctx = {
      surface: req.surface || 'drawer',
      lineKey: lineKey,
      lineNumber: identity.lineNumber,
      qty: req.qty,
      itemTitle: req.itemTitle || '',
      previousQty: req.previousQty,
      sourceEl: req.sourceEl || null
    };
    var isExplicitRemove = ctx.qty === 0 && !!ctx.itemTitle;
    var pending = _pending[lineKey];

    if (isExplicitRemove) {
      if (pending && pending.timer) clearTimeout(pending.timer);
      delete _pending[lineKey];
      executeUpdate(ctx);
      return;
    }

    if (!pending) {
      pending = _pending[lineKey] = ctx;
      pending.timer = null;
    } else {
      pending.qty = ctx.qty;
      pending.surface = ctx.surface;
      pending.itemTitle = ctx.itemTitle || pending.itemTitle || '';
      if (pending.previousQty == null && ctx.previousQty != null) pending.previousQty = ctx.previousQty;
      pending.sourceEl = ctx.sourceEl || pending.sourceEl;
      if (!pending.lineNumber && ctx.lineNumber) pending.lineNumber = ctx.lineNumber;
      if (pending.timer) clearTimeout(pending.timer);
    }

    pending.timer = setTimeout(function () {
      var latest = _pending[lineKey];
      if (!latest) return;
      delete _pending[lineKey];
      executeUpdate(latest);
    }, LINE_QTY_DEBOUNCE_MS);
  }

  window.DDLineMutation = {
    registerSurface: function (name, adapter) { _surfaces[name] = adapter || {}; },
    requestQtyChange: requestQtyChange,
    /* exposed for probes/debug */
    _getPendingKeys: function () { return Object.keys(_pending); }
  };
})();
